import { z } from 'zod';
import { AskAnswerSchema } from './ask';

/**
 * Legal quality review (M4/M6 gates, ADR-032): lawyers grade the system's answers to a fixed
 * question set, and a legal reviewer signs off a run. Grades are human judgements; nothing here
 * is produced by a model except the answers being graded.
 */

export const QualityQuestionKindSchema = z.enum([
  'answerable',
  'not_in_library',
  'conflicting',
  'amendment',
  'injection',
]);
export type QualityQuestionKind = z.infer<typeof QualityQuestionKindSchema>;

export const QualityQuestionSchema = z.object({
  id: z.string().regex(/^[a-z0-9-]{2,60}$/),
  area: z.string().trim().min(2).max(80),
  question: z.string().trim().min(5).max(1000),
  kind: QualityQuestionKindSchema,
  /** What a good answer must say or avoid, for the graders. Never sent to the model. */
  guidance: z.string().trim().max(2000).optional(),
});
export type QualityQuestion = z.infer<typeof QualityQuestionSchema>;

export const QualitySetSchema = z.object({
  id: z.string(),
  title: z.string(),
  questions: z.array(QualityQuestionSchema),
  createdAt: z.string(),
  createdByName: z.string().nullable(),
});
export type QualitySet = z.infer<typeof QualitySetSchema>;

export const CreateQualitySetRequestSchema = z
  .object({
    title: z.string().trim().min(3).max(200),
    questions: z.array(QualityQuestionSchema).min(1).max(60),
  })
  .strict();

export const QualityVerdictSchema = z.enum([
  'correct',
  'partly_correct',
  'incorrect',
  'correctly_declined',
  'should_have_answered',
]);
export type QualityVerdict = z.infer<typeof QualityVerdictSchema>;

export const CitationJudgementSchema = z.enum([
  'all_support',
  'some_unsupported',
  'none_support',
  'no_citations',
]);
export type CitationJudgement = z.infer<typeof CitationJudgementSchema>;

export const QualityGradeSchema = z.object({
  reviewerUid: z.string(),
  reviewerName: z.string().nullable(),
  verdict: QualityVerdictSchema,
  citations: CitationJudgementSchema,
  notes: z.string(),
  gradedAt: z.string(),
});
export type QualityGrade = z.infer<typeof QualityGradeSchema>;

export const GradeRequestSchema = z
  .object({
    verdict: QualityVerdictSchema,
    citations: CitationJudgementSchema,
    notes: z.string().trim().max(4000),
  })
  .strict();
export type GradeRequest = z.infer<typeof GradeRequestSchema>;

export const QualityItemSchema = z.object({
  question: QualityQuestionSchema,
  status: z.enum(['pending', 'answered', 'failed']),
  answer: AskAnswerSchema.nullable(),
  generatedAt: z.string().nullable(),
  grades: z.array(QualityGradeSchema),
});
export type QualityItem = z.infer<typeof QualityItemSchema>;

export const QualitySummarySchema = z.object({
  total: z.number().int(),
  answered: z.number().int(),
  graded: z.number().int(),
  /** Per verdict, counting each question by its most severe grade. */
  verdicts: z.partialRecord(QualityVerdictSchema, z.number().int()),
  citationsAllSupport: z.number().int(),
});
export type QualitySummary = z.infer<typeof QualitySummarySchema>;

export const SignOffSchema = z.object({
  byUid: z.string(),
  byName: z.string().nullable(),
  at: z.string(),
  statement: z.string(),
  summary: QualitySummarySchema,
});

export const QualityRunSchema = z.object({
  id: z.string(),
  setId: z.string(),
  title: z.string(),
  createdAt: z.string(),
  createdByName: z.string().nullable(),
  model: z.string(),
  signOff: SignOffSchema.nullable(),
  items: z.array(QualityItemSchema),
  summary: QualitySummarySchema,
  canGrade: z.boolean(),
  canManage: z.boolean(),
});
export type QualityRun = z.infer<typeof QualityRunSchema>;

export const QualityRunSummarySchema = QualityRunSchema.omit({
  items: true,
  canGrade: true,
  canManage: true,
});
export type QualityRunSummary = z.infer<typeof QualityRunSummarySchema>;

export const QualityOverviewSchema = z.object({
  sets: z.array(QualitySetSchema),
  runs: z.array(QualityRunSummarySchema),
  canManage: z.boolean(),
});
export type QualityOverview = z.infer<typeof QualityOverviewSchema>;

export const CreateRunRequestSchema = z.object({ setId: z.string().min(1).max(200) }).strict();

export const SignOffRequestSchema = z
  .object({ statement: z.string().trim().min(20).max(4000) })
  .strict();

/**
 * Lawyer-approved key points (ADR-028 decision 2, ADR-035). Drafted from an answer graded
 * correct in a review run, approved by a second lawyer, then given to the model for matching
 * questions to cover in its own words. Never shown to readers word for word.
 */
export const KeyPointPassageSchema = z.object({
  versionId: z.string(),
  passageId: z.string(),
  title: z.string(),
  label: z.string(),
  /** Quoted text from the answer it was drafted from, to find the passage's exact chunk. */
  quote: z.string().optional(),
});
export type KeyPointPassage = z.infer<typeof KeyPointPassageSchema>;

export const KeyPointSchema = z.object({
  text: z.string(),
  passages: z.array(KeyPointPassageSchema),
});
export type KeyPoint = z.infer<typeof KeyPointSchema>;

export const KeyPointStatusSchema = z.enum(['draft', 'approved', 'retired']);
export type KeyPointStatus = z.infer<typeof KeyPointStatusSchema>;

export const KeyPointSetSchema = z.object({
  id: z.string(),
  question: z.string(),
  points: z.array(KeyPointSchema),
  status: KeyPointStatusSchema,
  from: z.object({ runId: z.string(), questionId: z.string() }),
  createdAt: z.string(),
  createdByUid: z.string(),
  createdByName: z.string().nullable(),
  approvedAt: z.string().nullable(),
  approvedByName: z.string().nullable(),
  retiredAt: z.string().nullable(),
});
export type KeyPointSet = z.infer<typeof KeyPointSetSchema>;

export const KeyPointListSchema = z.object({
  sets: z.array(KeyPointSetSchema),
  canDraft: z.boolean(),
  canApprove: z.boolean(),
});
export type KeyPointList = z.infer<typeof KeyPointListSchema>;

/** Points are drafted from a graded answer: each names the answer's source numbers it rests on. */
export const DraftKeyPointsRequestSchema = z
  .object({
    runId: z.string().min(1).max(200),
    questionId: z.string().min(1).max(200),
    points: z
      .array(
        z
          .object({
            text: z.string().trim().min(5).max(600),
            sources: z.array(z.number().int().positive()).min(1).max(4),
          })
          .strict(),
      )
      .min(1)
      .max(8),
  })
  .strict();
export type DraftKeyPointsRequest = z.infer<typeof DraftKeyPointsRequestSchema>;
